/**
 * @tracetail/react - React Hooks for Enterprise Browser Fingerprinting
 * Version: 2.3.4
 *
 * Over 99.5% accuracy browser fingerprinting with React hooks and server-side processing.
 * Perfect for fraud detection, user analytics, and security applications.
 */

import { useEffect, useState, useCallback, useRef } from 'react';
import type { FC, ReactNode } from 'react';
import { TraceTail, FingerprintOptions, FingerprintResult } from '@tracetail/js';

export interface UseTraceTailOptions extends FingerprintOptions {
  immediate?: boolean;
  suspense?: boolean;
}

export interface UseTraceTailReturn {
  fingerprint: FingerprintResult | null;
  loading: boolean;
  error: Error | null;
  generate: () => Promise<FingerprintResult>;
  regenerate: () => Promise<FingerprintResult>;
}


/**
 * React hook for basic browser fingerprinting
 * 
 * @example
 * ```typescript
 * import { useTraceTail } from '@tracetail/react';
 * 
 * function MyComponent() {
 *   const { fingerprint, loading, error } = useTraceTail({
 *     apiKey: 'your-api-key',
 *     immediate: true
 *   });
 * 
 *   if (loading) return <div>Generating fingerprint...</div>;
 *   if (error) return <div>Error: {error.message}</div>;
 *   
 *   return <div>Visitor ID: {fingerprint?.visitorId}</div>;
 * }
 * ```
 */
export function useTraceTail(options: UseTraceTailOptions): UseTraceTailReturn {
  const [fingerprint, setFingerprint] = useState<FingerprintResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  
  const traceTailRef = useRef<TraceTail | null>(null);
  
  // Initialize TraceTail instance
  useEffect(() => {
    if (!traceTailRef.current) {
      traceTailRef.current = new TraceTail(options);
    }
  }, [options.apiKey, options.endpoint]);

  const generate = useCallback(async (): Promise<FingerprintResult> => {
    if (!traceTailRef.current) {
      throw new Error('TraceTail not initialized');
    }

    setLoading(true);
    setError(null);

    try {
      const result = await traceTailRef.current.generateFingerprint({
        verbose: true
      });
      setFingerprint(result);
      return result;
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Unknown error');
      setError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  const regenerate = useCallback(async (): Promise<FingerprintResult> => {
    setFingerprint(null);
    return generate();
  }, [generate]);

  // Auto-generate on mount if immediate is true
  useEffect(() => {
    if (options.immediate && !fingerprint && !loading) {
      generate().catch(() => {
        // Error is already handled in generate function
      });
    }
  }, [options.immediate, fingerprint, loading, generate]);

  return {
    fingerprint,
    loading,
    error,
    generate,
    regenerate
  };
}


/**
 * React hook for visitor identification with persistent storage
 * 
 * @example
 * ```typescript
 * import { useVisitorId } from '@tracetail/react';
 * 
 * function UserTrackingComponent() {
 *   const { visitorId, isReturningVisitor, loading } = useVisitorId({
 *     apiKey: 'your-api-key',
 *     storageKey: 'app_visitor_id'
 *   });
 * 
 *   return (
 *     <div>
 *       <p>Visitor: {visitorId}</p>
 *       <p>Status: {isReturningVisitor ? 'Returning' : 'New'} visitor</p>
 *     </div>
 *   );
 * }
 * ```
 */
export function useVisitorId(options: UseTraceTailOptions & {
  storageKey?: string;
}): {
  visitorId: string | null;
  isReturningVisitor: boolean;
  loading: boolean;
  error: Error | null;
  refresh: () => Promise<void>;
} {
  const storageKey = options.storageKey || 'tracetail_visitor_id';
  const [visitorId, setVisitorId] = useState<string | null>(null);
  const [isReturningVisitor, setIsReturningVisitor] = useState(false);
  
  const { fingerprint, loading, error, generate } = useTraceTail({
    ...options,
    immediate: true
  });

  // Update visitor ID when fingerprint changes
  useEffect(() => {
    if (fingerprint?.visitorId) {
      const storedVisitorId = localStorage.getItem(storageKey);
      
      if (storedVisitorId) {
        setIsReturningVisitor(storedVisitorId === fingerprint.visitorId);
      } else {
        setIsReturningVisitor(false);
      }
      
      setVisitorId(fingerprint.visitorId);
      localStorage.setItem(storageKey, fingerprint.visitorId);
    }
  }, [fingerprint?.visitorId, storageKey]);

  const refresh = useCallback(async () => {
    await generate();
  }, [generate]);

  return {
    visitorId,
    isReturningVisitor,
    loading,
    error,
    refresh
  };
}


// Re-export types and utilities from the base package
export type {
  FingerprintOptions,
  FingerprintResult,
  ComponentData
} from '@tracetail/js';

export { TraceTail } from '@tracetail/js';

// React-specific utilities
interface TraceTailProviderProps {
  children: ReactNode;
  apiKey: string;
  options?: Partial<FingerprintOptions>;
}

export const TraceTailProvider: FC<TraceTailProviderProps> = ({ children }) => {
  // This is a simple provider that could be extended with context
  // For now, it just renders children since hooks manage their own instances
  return <>{children}</>;
};

/**
 * Get the current React package version
 */
export const getVersion = (): string => '2.3.4';

// Default export
export default {
  useTraceTail,
  useVisitorId,
  TraceTailProvider,
  getVersion
};